import { type OnChangeFn, type SortingState } from '@tanstack/react-table';
import { Map, Table2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { ErrorState } from '@/components/common';
import { FilterPanel } from '@/components/filter-panel';
import { OpportunityMapView } from '@/components/opportunity-map';
import { OpportunityTable } from '@/components/opportunity-table';
import { DataPagination } from '@/components/pagination';
import { DashboardStats } from '@/components/stats';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useOpportunities,
  useOpportunityMap,
  type OpportunityMapResponse,
} from '@/hooks/api/useOpportunities';
import { type SortField, useOpportunityFilters } from '@/hooks/useOpportunityFilters';
import { formatCurrencyCompact, formatNumber } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useTheme } from '@/providers/theme-provider';

import { DashboardSkeleton } from './DashboardSkeleton';

const SORT_FIELD_BY_COLUMN: Record<string, SortField> = {
  propensityScore: 'propensity_score',
  clientName: 'client_name',
  policyNumber: 'policy_number',
  carrierName: 'carrier_name',
  accountValue: 'account_value',
  surrenderLossPercentage: 'surrender_loss',
  estimatedValue: 'estimated_value',
  agentName: 'agent_name',
  ownerAge: 'client_age',
  surrenderDate: 'surrender_date',
  incomeBenefitValue: 'income_benefit_value',
  salesTeam: 'sales_team_name',
  taxQualification: 'tax_qualification',
  forwardedAt: 'forwarded_at',
  forwardedBy: 'forwarded_by',
  status: 'status',
  notes: 'has_notes',
};

const COLUMN_BY_SORT_FIELD: Partial<Record<SortField, string>> = {
  propensity_score: 'propensityScore',
  estimated_value: 'estimatedValue',
  account_value: 'accountValue',
  carrier_name: 'carrierName',
  surrender_loss: 'surrenderLossPercentage',
  agent_name: 'agentName',
  client_age: 'ownerAge',
  surrender_date: 'surrenderDate',
  sales_team_name: 'salesTeam',
  client_name: 'clientName',
  policy_number: 'policyNumber',
  income_benefit_value: 'incomeBenefitValue',
  tax_qualification: 'taxQualification',
  forwarded_at: 'forwardedAt',
  forwarded_by: 'forwardedBy',
  status: 'status',
  has_notes: 'notes',
};

export function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDarkMap = theme === 'dark';
  const isMapView = location.pathname === '/map';
  const salesTeamOptions = useMemo(
    () =>
      (user?.salesTeams ?? []).map((team) => ({
        value: team.salesTeamId,
        label: team.salesTeamName,
      })),
    [user?.salesTeams]
  );
  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    queryFilters,
    setPage,
    setPageSize,
    setSort,
    setFilter,
    clearFilters,
  } = useOpportunityFilters();

  const mapFilters = useMemo(
    () => ({
      salesTeamId: filters.salesTeamId,
      advisorId: filters.advisorId,
      carrierName: filters.carrierName,
      productType: filters.productType,
      outOfSurrender: filters.outOfSurrender,
      minScore: filters.minScore,
      maxScore: filters.maxScore,
      minSurrenderLoss: filters.minSurrenderLoss,
      maxSurrenderLoss: filters.maxSurrenderLoss,
      status: filters.status,
      limit: 5000,
    }),
    [filters]
  );
  const { data, isLoading, error, refetch } = useOpportunities(queryFilters, {
    enabled: !isMapView,
  });
  const {
    data: mapData,
    isLoading: isMapLoading,
    error: mapError,
    refetch: refetchMap,
  } = useOpportunityMap(mapFilters, {
    enabled: isMapView,
  });
  const sorting = useMemo<SortingState>(() => {
    const columnId = COLUMN_BY_SORT_FIELD[sortBy];
    return columnId ? [{ id: columnId, desc: sortOrder === 'desc' }] : [];
  }, [sortBy, sortOrder]);
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
      const next = nextSorting[0];

      if (!next) {
        setSort('propensity_score', 'desc');
        return;
      }

      const nextSortField = SORT_FIELD_BY_COLUMN[next.id];
      if (nextSortField) {
        setSort(nextSortField, next.desc ? 'desc' : 'asc');
      }
    },
    [setSort, sorting]
  );
  const handleViewChange = useCallback(
    (view: 'table' | 'map') => {
      navigate({
        pathname: view === 'map' ? '/map' : '/opportunities',
        search: location.search,
      });
    },
    [location.search, navigate]
  );

  if (!isMapView && isLoading && !data) {
    return <DashboardSkeleton />;
  }

  if (!isMapView && error) {
    return (
      <div className="py-8">
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load opportunities'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (isMapView) {
    return (
      <div
        className={cn(
          '-mx-4 -my-4 h-[calc(100svh-3rem)] min-h-[560px] overflow-hidden lg:-mx-6',
          'bg-background'
        )}
      >
        <div className="relative h-full">
          <OpportunityMapView
            data={mapData}
            isLoading={isMapLoading}
            error={mapError}
            onRetry={() => void refetchMap()}
            fullBleed
            theme={theme}
          />

          <div className="pointer-events-none absolute left-3 right-3 top-3 z-30 sm:left-4 sm:right-4 sm:top-4 xl:left-6 xl:right-6">
            <div className="flex items-stretch gap-0">
              <div className="pointer-events-auto flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <h1
                    className={cn(
                      'text-3xl font-semibold tracking-tight',
                      isDarkMap ? 'text-white' : 'text-foreground'
                    )}
                  >
                    Opportunities
                  </h1>
                  <ViewToggle view="map" onChange={handleViewChange} variant="default" />
                </div>
                <FilterPanel
                  filters={filters}
                  onFilterChange={setFilter}
                  onClearFilters={clearFilters}
                  variant="surface"
                  salesTeamOptions={salesTeamOptions}
                />
              </div>
              <div className="pointer-events-auto relative z-10 hidden w-72 shrink-0 pl-6 lg:block">
                <div
                  className="pointer-events-none absolute inset-y-0 -left-6 w-12"
                  style={{
                    background: 'linear-gradient(to right, transparent, hsl(var(--background)))',
                  }}
                />
                <MapSummaryCard data={mapData} isLoading={isMapLoading} theme={theme} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Title top-left, filters bottom-left, stats card right */}
      <div className="flex items-stretch gap-0">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-3xl font-semibold tracking-tight">Opportunities</h1>
            <ViewToggle view={isMapView ? 'map' : 'table'} onChange={handleViewChange} />
          </div>
          <FilterPanel
            filters={filters}
            onFilterChange={setFilter}
            onClearFilters={clearFilters}
            salesTeamOptions={salesTeamOptions}
          />
        </div>
        {/* Summary card with gradient fade on its left edge */}
        <div className="relative z-10 w-72 shrink-0 pl-6">
          <div
            className="pointer-events-none absolute inset-y-0 -left-6 w-12"
            style={{
              background: 'linear-gradient(to right, transparent, hsl(var(--surface-1)))',
            }}
          />
          <DashboardStats filters={queryFilters} />
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-elevation-1">
        <OpportunityTable
          data={data?.data ?? []}
          isLoading={isLoading}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onRowClick={(row) => navigate(`/opportunities/${row.id}`)}
          sentToAgentPlatform={filters.status === 'actioned'}
        />
        {data && data.pagination.totalPages > 1 && (
          <DataPagination
            page={page}
            pageSize={pageSize}
            totalCount={data.pagination.totalCount}
            totalPages={data.pagination.totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>
    </div>
  );
}

function MapSummaryCard({
  data,
  isLoading,
  theme,
}: {
  data?: OpportunityMapResponse;
  isLoading: boolean;
  theme: 'light' | 'dark';
}) {
  const isDark = theme === 'dark';
  const loadingValue = isLoading && !data ? 'Loading' : undefined;
  const items = [
    {
      label: 'Showing',
      value: loadingValue ?? formatNumber(data?.returnedCount),
    },
    {
      label: 'Total Opportunities',
      value: loadingValue ?? formatNumber(data?.totalCount),
    },
    {
      label: 'Top Est. Commission',
      value: loadingValue ?? formatCurrencyCompact(data?.domains.estimatedValue.max),
    },
  ];

  return (
    <Card
      className="bg-gradient-card text-card-foreground backdrop-blur-xl"
      style={{ boxShadow: 'var(--shadow-1), var(--inset-highlight)' }}
    >
      <CardContent className="space-y-1 px-4 py-2.5">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between gap-3">
            <span className="truncate text-xs text-muted-foreground">{item.label}</span>
            <span className="shrink-0 font-mono text-sm font-semibold tracking-tight text-foreground">
              {item.value}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Score Colors</span>
          <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
            <ScoreLegendDot className="bg-slate-400" label=">75" />
            <ScoreLegendDot className="bg-[#ff8a1f]" label="<75" />
          </div>
        </div>
        {data?.limited && (
          <div className={cn('pt-1 text-[11px]', isDark ? 'text-amber-200' : 'text-amber-700')}>
            Showing top {formatNumber(data.maxReturned)} by estimated commission.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScoreLegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('h-2 w-2 rounded-full', className)} />
      {label}
    </span>
  );
}

function ViewToggle({
  view,
  onChange,
  variant = 'default',
}: {
  view: 'table' | 'map';
  onChange: (view: 'table' | 'map') => void;
  variant?: 'default' | 'overlay';
}) {
  const overlay = variant === 'overlay';
  const options = [
    { value: 'table' as const, label: 'Table', icon: Table2 },
    { value: 'map' as const, label: 'Map', icon: Map },
  ];

  return (
    <div
      className={cn(
        'inline-flex h-8 shrink-0 items-center rounded-md p-0.5',
        overlay && 'bg-white/10'
      )}
      style={overlay ? undefined : { backgroundColor: 'hsl(var(--muted))' }}
    >
      {options.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-7 gap-1.5 rounded px-2 text-xs font-medium shadow-none hover:bg-background/80',
            view === option.value && overlay
              ? 'bg-white/20 text-white shadow-sm hover:bg-white/25'
              : view === option.value
                ? 'bg-background text-foreground shadow-sm'
                : overlay
                  ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                  : 'text-muted-foreground hover:text-foreground'
          )}
          style={
            view === option.value && !overlay
              ? { backgroundColor: 'hsl(var(--background))' }
              : undefined
          }
          onClick={() => onChange(option.value)}
        >
          <option.icon className="h-3.5 w-3.5" />
          {option.label}
        </Button>
      ))}
    </div>
  );
}
